import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiErr } from "@/lib/apiClient";
import { toast } from "sonner";
import { GraduationCap, Wrench, FolderGit2, Building2, Award, Users, Plus, X, Search, Gauge, Trash2, Loader2 } from "lucide-react";

const CATS = [
  { id: "academics", label: "Academics", icon: GraduationCap },
  { id: "skills", label: "Technical Skills & Tools", icon: Wrench },
  { id: "projects", label: "Projects", icon: FolderGit2 },
  { id: "internships", label: "Internships & Industry Exposure", icon: Building2 },
  { id: "certifications", label: "Certifications & Courses", icon: Award },
  { id: "leadership", label: "Leadership & Extracurriculars", icon: Users },
];

const REL = [
  { v: "direct", l: "Directly relevant" },
  { v: "somewhat", l: "Somewhat relevant" },
  { v: "tangential", l: "Tangential" },
];
const DEPTH = [
  { v: "course", l: "Course / academic submission" },
  { v: "personal", l: "Personal / club project" },
  { v: "published", l: "Fabricated / published / competition" },
];
const TIER = [
  { v: "startup", l: "Startup / early-stage" },
  { v: "mid", l: "Mid-size company" },
  { v: "large", l: "Large / MNC" },
];

const inputCls = "w-full px-4 py-2.5 rounded-lg border border-slate-300 text-slate-900 text-sm focus:border-[#6366F1] focus:ring-2 focus:ring-[#6366F1]/20 outline-none transition-colors";
const selCls = "px-3 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm bg-white";

const LevelPill = ({ level }) => (
  <span className={`text-[11px] font-medium px-2.5 py-1 rounded-full border ${
    level === "production" ? "bg-green-100 text-green-700 border-green-200" : "bg-amber-100 text-amber-700 border-amber-200"
  }`}>{level === "production" ? "Production-ready" : "Practicing"}</span>
);

export default function ProfileBuilder({ trackId, careerName, onReadiness }) {
  const [tab, setTab] = useState("skills");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const { data } = await api.get(`/tracks/${trackId}/profile`);
    setData(data);
    setLoading(false);
    cbRef.current?.(data.readiness);
  }, [trackId]);
  const cbRef = useRef(onReadiness);
  useEffect(() => { cbRef.current = onReadiness; }, [onReadiness]);

  useEffect(() => { setLoading(true); load(); }, [trackId, load]);

  if (loading || !data) return <div className="bg-white rounded-2xl p-10 border border-slate-200 flex justify-center"><Loader2 className="animate-spin text-[#6366F1]" /></div>;

  const score = data.readiness.score;
  const scoreColor = score >= 70 ? "#22C55E" : score >= 40 ? "#6366F1" : "#F59E0B";

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="profile-builder">
      {/* Header with live readiness */}
      <div className="flex items-start justify-between gap-4 p-6 pb-4 border-b border-slate-100">
        <div>
          <p className="eyebrow text-[#6366F1]">BUILD YOUR PROFILE · {careerName}</p>
          <h3 className="font-display font-extrabold text-xl text-slate-900 tracking-tight mt-1">Everything you add is scored against this track</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">Be honest — proficiency and depth matter more than count. Each career values these categories differently.</p>
        </div>
        <div className="shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl border-2" style={{ borderColor: scoreColor }} data-testid="live-readiness">
          <Gauge size={20} style={{ color: scoreColor }} />
          <div className="leading-none">
            <p className="font-mono text-2xl font-bold" style={{ color: scoreColor }}>{score}</p>
            <p className="eyebrow text-slate-400 mt-0.5">LIVE READINESS</p>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 px-6 py-4">
        {CATS.map((c, i) => {
          const active = tab === c.id;
          const comp = data.readiness.components.find((x) => x.key === c.id);
          return (
            <button key={c.id} data-testid={`pb-tab-${c.id}`} onClick={() => setTab(c.id)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${
                active ? "border-[#6366F1] bg-[#6366F1]/5 text-[#4F46E5]" : "border-slate-200 text-slate-500 hover:border-slate-300"
              }`}>
              <span className="font-mono text-xs text-slate-400">{i + 1}</span> {c.label}
              {comp && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{Math.round(comp.fraction * 100)}%</span>}
            </button>
          );
        })}
      </div>

      <div className="px-6 pb-6">
        {tab === "skills" && <SkillsTab data={data} reload={load} careerName={careerName} />}
        {tab === "academics" && <SimpleTab kind="academics" trackId={trackId} items={data.items.academics} reload={load}
          title="Relevant coursework & academics" hint="Add subjects or coursework that a recruiter for this track would value. Your CGPA already seeds this category."
          placeholder="e.g. Thermodynamics, Machine Design" />}
        {tab === "projects" && <ProjectsTab trackId={trackId} items={data.items.projects} reload={load} />}
        {tab === "internships" && <InternshipsTab trackId={trackId} items={data.items.internships} reload={load} />}
        {tab === "certifications" && <SimpleTab kind="certifications" trackId={trackId} items={data.items.certifications} reload={load} withRelevance
          title="Certifications & courses" hint="Only credentials a recruiter for this track would recognise are worth full weight."
          placeholder="e.g. CSWA, Six Sigma Green Belt" />}
        {tab === "leadership" && <SimpleTab kind="leadership" trackId={trackId} items={data.items.leadership} reload={load} withRelevance
          title="Leadership & extracurriculars" hint="Roles that show ownership and initiative relevant to this track."
          placeholder="e.g. Baja team captain, Robotics club lead" />}
      </div>
    </div>
  );
}

function SkillsTab({ data, reload }) {
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [level, setLevel] = useState("basic");
  const [busy, setBusy] = useState(false);

  // Only skills this track actually values (from the career skill map) that aren't added yet.
  const available = data.readiness.skills
    .filter((s) => s.user_level === "none")
    .filter((s) => !q || s.skill_name.toLowerCase().includes(q.toLowerCase()));

  const add = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.post("/self-assessment", { assessments: [{ skill_id: sel.skill_id, level }] });
      setSel(null); setQ("");
      await reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (skillId) => {
    try { await api.delete(`/skills/${skillId}/acquired`); await reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <h4 className="font-bold text-slate-900">Technical skills and tools</h4>
      <p className="text-xs text-slate-500 mb-4">Only skills this track values move the score, and proficiency matters more than count. Re-adding a skill just updates its level.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <label className="text-sm font-medium text-slate-700">Skill</label>
          {sel ? (
            <div className="mt-1"><span className="inline-flex items-center gap-1.5 bg-[#6366F1] text-white text-sm px-3 py-2 rounded-lg">{sel.skill_name} <button onClick={() => setSel(null)}><X size={14} /></button></span></div>
          ) : (
            <div className="relative mt-1">
              <Search size={16} className="absolute left-3 top-3 text-slate-400" />
              <input data-testid="pb-skill-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Choose a skill this track needs" className={`${inputCls} pl-9`} />
              {q && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-44 overflow-y-auto">
                  {available.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-slate-400">No matching unadded skills for this track.</p>
                  ) : available.slice(0, 8).map((s) => (
                    <button key={s.skill_id} data-testid={`pb-skill-opt-${s.skill_id}`} onClick={() => { setSel(s); setQ(s.skill_name); }} className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center justify-between">
                      {s.skill_name}{s.essential && <span className="text-[9px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">ESSENTIAL</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block">Proficiency</label>
          <select data-testid="pb-skill-level" value={level} onChange={(e) => setLevel(e.target.value)} className={`${selCls} mt-1`}>
            <option value="basic">Practicing</option>
            <option value="production">Production-ready</option>
          </select>
        </div>
        <button data-testid="pb-add-skill" onClick={add} disabled={!sel || busy}
          className="inline-flex items-center gap-1.5 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add skill
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {data.added_skills.length === 0 ? (
          <p className="text-sm text-slate-400">No skills added yet.</p>
        ) : data.added_skills.map((s) => (
          <div key={s.skill_id} data-testid={`pb-skill-row-${s.skill_id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-semibold text-slate-800 text-sm truncate">{s.skill_name}</p>
              {s.essential && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">ESSENTIAL</span>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <LevelPill level={s.level} />
              <button onClick={() => remove(s.skill_id)} data-testid={`pb-skill-remove-${s.skill_id}`} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ItemList({ items, reload }) {
  const remove = async (id) => {
    try { const { data } = await api.delete(`/profile-items/${id}`); await reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!items || items.length === 0) return <p className="text-sm text-slate-400 mt-4">Nothing added yet.</p>;
  return (
    <div className="mt-4 space-y-2">
      {items.map((it) => (
        <div key={it.id} data-testid={`pb-item-${it.id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-slate-200">
          <div className="min-w-0">
            <p className="font-semibold text-slate-800 text-sm truncate">{it.Title}</p>
            <p className="text-[11px] text-slate-400">
              {[it.depth && DEPTH.find((d) => d.v === it.depth)?.l, it.weeks && `${it.weeks} wks`,
                it.tier && TIER.find((t) => t.v === it.tier)?.l, REL.find((r) => r.v === it.relevance)?.l].filter(Boolean).join(" · ")}
            </p>
          </div>
          <button onClick={() => remove(it.id)} data-testid={`pb-item-remove-${it.id}`} className="text-slate-300 hover:text-red-500 transition-colors shrink-0"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
}

function AddBtn({ onClick, busy, label = "Add" }) {
  return (
    <button data-testid="pb-add-item" onClick={onClick} disabled={busy}
      className="inline-flex items-center gap-1.5 bg-[#6366F1] hover:bg-[#4F46E5] active:scale-[0.98] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {label}
    </button>
  );
}

function SimpleTab({ kind, trackId, items, reload, title, hint, placeholder, withRelevance }) {
  const [title2, setTitle2] = useState("");
  const [rel, setRel] = useState("direct");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!title2.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tracks/${trackId}/profile`, { category: kind, title: title2, relevance: rel });
      setTitle2("");
      await reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <h4 className="font-bold text-slate-900">{title}</h4>
      <p className="text-xs text-slate-500 mb-4">{hint}</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <label className="text-sm font-medium text-slate-700">{kind === "certifications" ? "Certification" : kind === "leadership" ? "Role / activity" : "Title"}</label>
          <input data-testid="pb-item-title" value={title2} onChange={(e) => setTitle2(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />
        </div>
        {withRelevance && (
          <div>
            <label className="text-sm font-medium text-slate-700 block">Relevance to track</label>
            <select data-testid="pb-item-relevance" value={rel} onChange={(e) => setRel(e.target.value)} className={`${selCls} mt-1`}>
              {REL.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
        )}
        <AddBtn onClick={add} busy={busy} />
      </div>
      <ItemList items={items} reload={reload} />
    </div>
  );
}

function ProjectsTab({ trackId, items, reload }) {
  const [title, setTitle] = useState("");
  const [depth, setDepth] = useState("course");
  const [rel, setRel] = useState("direct");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!title.trim()) return;
    setBusy(true);
    try { await api.post(`/tracks/${trackId}/profile`, { category: "projects", title, depth, relevance: rel }); setTitle(""); await reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <h4 className="font-bold text-slate-900">Projects</h4>
      <p className="text-xs text-slate-500 mb-4">Depth is the multiplier: a fabricated or published project scores up to three times a course submission.</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-sm font-medium text-slate-700">Project title</label>
          <input data-testid="pb-item-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Baja suspension upright redesign" className={`${inputCls} mt-1`} />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block">Depth</label>
          <select data-testid="pb-project-depth" value={depth} onChange={(e) => setDepth(e.target.value)} className={`${selCls} mt-1`}>
            {DEPTH.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block">Relevance to track</label>
          <select data-testid="pb-item-relevance" value={rel} onChange={(e) => setRel(e.target.value)} className={`${selCls} mt-1`}>
            {REL.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
        <AddBtn onClick={add} busy={busy} />
      </div>
      <ItemList items={items} reload={reload} />
    </div>
  );
}

function InternshipsTab({ trackId, items, reload }) {
  const [org, setOrg] = useState("");
  const [weeks, setWeeks] = useState(6);
  const [rel, setRel] = useState("direct");
  const [tier, setTier] = useState("mid");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!org.trim()) return;
    setBusy(true);
    try { await api.post(`/tracks/${trackId}/profile`, { category: "internships", title: org, weeks: Number(weeks), relevance: rel, tier }); setOrg(""); await reload(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div>
      <h4 className="font-bold text-slate-900">Internships and industry exposure</h4>
      <p className="text-xs text-slate-500 mb-4">Eight weeks counts as one full unit of exposure; relevance and employer tier scale it up or down.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium text-slate-700">Organisation</label>
          <input data-testid="pb-item-title" value={org} onChange={(e) => setOrg(e.target.value)} placeholder="e.g. Bosch, Pune" className={`${inputCls} mt-1`} />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700">Duration (weeks)</label>
          <input data-testid="pb-intern-weeks" type="number" min="1" value={weeks} onChange={(e) => setWeeks(e.target.value)} className={`${inputCls} mt-1`} />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block">Relevance to track</label>
          <select data-testid="pb-item-relevance" value={rel} onChange={(e) => setRel(e.target.value)} className={`${selCls} mt-1 w-full`}>
            {REL.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-700 block">Employer tier</label>
          <select data-testid="pb-intern-tier" value={tier} onChange={(e) => setTier(e.target.value)} className={`${selCls} mt-1 w-full`}>
            {TIER.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </div>
      </div>
      <div className="mt-3"><AddBtn onClick={add} busy={busy} label="Add internship" /></div>
      <ItemList items={items} reload={reload} />
    </div>
  );
}
